import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import toArray from 'lodash/toArray';

import { PARENT_PROFESSION_SLUG, REPERTOIRE_SOURCE, futureRepertoireStatuses } from 'constants/consts';
import { REPERTOIRE_STATUSES, SORT_OPTION_TYPES } from 'constants/index';

import { getOption } from 'utils/globals/app';
import { condenseImageObject, expandImageObject } from 'utils/globals/imageOptimizatons';

import { ENTITY_TYPES, PROFESSION_TYPES } from 'operabase-router/constants';

// TODO: we need to optimise below method
const groupRepertoireRecords = (repertoire, sortComposerAsc = true) => {
  const { orderedComposers, groupedByComposer } =
    repertoire?.reduce(
      (acc, item) => {
        const composer = item?.work?.creators?.find(creator => creator?.profession?.slug === 'composer');

        acc.orderedComposers.add(composer?.profile?.shortName);
        acc.groupedByComposer[composer?.profile?.id] = [
          ...(acc.groupedByComposer?.[composer?.profile?.id] || []),
          item,
        ];

        return acc;
      },
      { orderedComposers: new Set(), groupedByComposer: {} },
    ) || {};

  const data = {};
  const workRoles = [];
  Object.keys(groupedByComposer).map(key => {
    const works = get(groupedByComposer, key);
    let composer = get(works, '[0].work.creators');
    composer = composer?.filter(creator => creator?.profession?.slug === 'composer');
    const groupedWorks = groupBy(works, 'work.id');
    const worksStructured = {};

    if (composer?.length) {
      Object.keys(groupedWorks).map(workId => {
        const roles = get(groupedWorks, workId);
        const workInfo = get(roles, '[0].work');
        const list = [];
        roles.map((role, pointer) => {
          if (role?.productionsTotal > 0 || futureRepertoireStatuses?.includes(role?.status)) {
            workRoles.push(`${workInfo?.id}_${pointer}`);

            list.push(role);
          }

          return list;
        });

        set(worksStructured, workId, {
          work: workInfo,
          data: toArray(list),
        });
        return worksStructured;
      });
      set(data, [composer?.[0]?.profile?.shortName], {
        composer,
        data: sortBy(toArray(worksStructured), r => r?.work?.name?.toUpperCase()),
      });
    }

    return data;
  });
  let sortedComposers = Array.from(orderedComposers)?.sort();

  if (!sortComposerAsc) {
    sortedComposers = sortedComposers.reverse();
  }

  const result = {
    composerNames: sortedComposers,
    workRoles,
    data,
  };

  return result;
};

const extractRepertoireDetails = (repertoireRecord, sortComposerAsc, limit) => {
  const combinedRepertoire =
    repertoireRecord?.reduce((result, rep) => {
      if (rep?.source === REPERTOIRE_SOURCE.USER || rep?.source === REPERTOIRE_SOURCE.PERFORMANCE) {
        // NOTE: If it's a future repertoire with source USER, add isFuture flag
        if (futureRepertoireStatuses?.includes(rep?.status) && rep?.source === REPERTOIRE_SOURCE.USER) {
          result.push({ ...rep, isFuture: true });
        } else {
          result.push(rep);
        }
      }

      return result;
    }, []) || [];

  const limitedRepertoire = limit > 0 ? combinedRepertoire?.slice(0, limit) : combinedRepertoire;

  return {
    repertoire: groupRepertoireRecords(limitedRepertoire, sortComposerAsc),
    repertoireCount: combinedRepertoire?.length,
  };
};

// TODO: Remove this for casting view and modify transformRepertoireData
export const transformCastingViewRepertoireData = (response, props) =>
  extractRepertoireDetails(
    response?.data,
    props?.filters?.sort === SORT_OPTION_TYPES.COMPOSERS_ASC.value,
    props?.limit,
  );

export const transformRepertoireData = response => {
  const futureRepertoire = {};
  const scheduleRepertoire = {};
  let futureRepertoireCount = 0;
  let scheduleRepertoireCount = 0;

  // eslint-disable-next-line no-unused-expressions
  response?.data?.forEach(repertoire => {
    const composer = repertoire?.work?.creators?.filter(creator => creator?.profession?.slug === 'composer');
    const composerName = composer?.[0]?.profile?.shortName;
    const status = repertoire?.status;
    const workRoleName = repertoire?.workRole?.name;
    const workProfessionName = repertoire?.profession?.name;

    if (composerName) {
      const isFutureRepertoire =
        futureRepertoireStatuses.includes(status) && repertoire?.source === REPERTOIRE_SOURCE.USER;
      const isScheduleRepertoire =
        !futureRepertoireStatuses.includes(status) &&
        (repertoire?.source === REPERTOIRE_SOURCE.USER || repertoire?.source === REPERTOIRE_SOURCE.PERFORMANCE);

      if (isFutureRepertoire) {
        futureRepertoireCount += 1;
        futureRepertoire[composerName] = futureRepertoire[composerName] || { data: [], composer };
        futureRepertoire[composerName].data.push({
          id: repertoire?.id,
          name: repertoire?.work?.original_name,
          roles: workRoleName ? [{ roleName: workRoleName, status }] : [],
          professionNames: workProfessionName ? [{ professionName: workProfessionName, status }] : [],
        });
      } else if (isScheduleRepertoire) {
        scheduleRepertoireCount += 1;
        scheduleRepertoire[composerName] = scheduleRepertoire[composerName] || { data: [], composer };
        const existingWork = scheduleRepertoire[composerName].data.find(
          work => work.name === repertoire?.work?.original_name,
        );

        if (existingWork) {
          if (workRoleName) {
            const updatedWorkRoles = Array.isArray(existingWork.roles) ? [...existingWork.roles] : [];
            const isWorkRoleExist = updatedWorkRoles.some(item => item?.roleName === workRoleName);

            if (workRoleName && !isWorkRoleExist) {
              updatedWorkRoles.push({
                roleName: workRoleName,
                status,
              });
            }

            existingWork.roles = updatedWorkRoles;
          } else if (workProfessionName) {
            const updatedProfessionNames = Array.isArray(existingWork.professionNames)
              ? [...existingWork.professionNames]
              : [];
            const isProfessionExist = updatedProfessionNames.some(item => item?.professionName === workProfessionName);

            if (workProfessionName && !isProfessionExist) {
              updatedProfessionNames.push({
                professionName: workProfessionName,
                status,
              });
            }

            existingWork.professionNames = updatedProfessionNames;
          }
        } else {
          scheduleRepertoire[composerName].data.push({
            id: repertoire?.id,
            name: repertoire?.work?.original_name,
            roles: workRoleName ? [{ roleName: workRoleName, status }] : [],
            professionNames: workProfessionName ? [{ professionName: workProfessionName, status }] : [],
          });
        }
      }
    }
  });

  return { futureRepertoire, scheduleRepertoire, futureRepertoireCount, scheduleRepertoireCount };
};

export const transformedSuggestedData = response =>
  response.reduce((groupedData, obj) => {
    const key = Object.keys(obj)?.[0];
    const value = Object.values(obj)?.[0];

    return {
      ...groupedData,
      [key]: [...(groupedData[key] || []), { entity: value, entityType: key }],
    };
  }, {});

export const selectTrendingArtistProfessions = (response, limit) => {
  const sortedOptions = [
    ...(response?.data?.[PROFESSION_TYPES.PROFESSION] || []),
    ...(response?.data?.[PROFESSION_TYPES.VOICE_TYPE] || []),
    ...(response?.data?.[PROFESSION_TYPES.INSTRUMENTALIST] || []),
  ]
    ?.filter(({ pro }) => pro >= 8)
    ?.sort(({ order: orderA }, { order: orderB }) => orderA - orderB)
    ?.slice(0, limit);

  return sortedOptions;
};

export const transformRepertoireProdData = (productions = [], entityId, repertoireData = []) => {
  const repertoireMap = new Map();
  const groupedByComposer = {};
  const composerNamesSet = new Set();
  const workRoles = [];
  const seenWorkRoleProfiles = new Set();

  // Group repertoireData by work.id
  if (Array.isArray(repertoireData)) {
    repertoireData.forEach(item => {
      const workId = item.work?.id;

      if (workId) {
        if (!repertoireMap.has(workId)) {
          repertoireMap.set(workId, []);
        }

        repertoireMap.get(workId).push(item);
      }
    });
  }

  const combinedEntries = [];

  productions.forEach(production => {
    const workWrappers = production.productionWorks || [];
    const contributions = production.contributions || [];

    workWrappers.forEach(wrapper => {
      const { work } = wrapper || {};

      if (!work?.id) {
        return;
      }

      // Inject workType
      if (wrapper.productionWorkToWorkTypes?.[0]?.workType) {
        work.workType = wrapper.productionWorkToWorkTypes[0].workType;
      }

      contributions.forEach(contribution => {
        const profileId = contribution.profile?.id;
        const contribWorkId = contribution.productionWork?.work?.id;

        if (profileId !== entityId || contribWorkId !== work.id) {
          return;
        }

        const workRoleId = contribution.workRole?.id || 0;
        const key = `${work.id}_${workRoleId}_${profileId}`;

        if (!seenWorkRoleProfiles.has(key)) {
          seenWorkRoleProfiles.add(key);

          // Find matching repertoire item to get status
          const repertoireItems = repertoireMap.get(work.id) || [];
          const matchingRepertoire = repertoireItems.find(
            rep => rep.workRole?.id === contribution.workRole?.id && rep.profile?.id === contribution.profile?.id,
          );

          combinedEntries.push({
            ...contribution,
            work,
            productionsTotal: 1,
            performancesTotal: (contribution.performances || []).length,
            productionIds: [production.id],
            status: matchingRepertoire?.status || null,
          });
        } else {
          const existing = combinedEntries.find(
            e => e.work?.id === work.id && e.workRole?.id === workRoleId && e.profile?.id === profileId,
          );

          if (existing) {
            existing.productionsTotal += 1;
            existing.performancesTotal += (contribution.performances || []).length;

            if (!existing.productionIds.includes(production.id)) {
              existing.productionIds.push(production.id);
            }
          }
        }
      });
    });
  });

  // Merge or append repertoireData entries with special statuses even if not in productions
  if (Array.isArray(repertoireData)) {
    repertoireData.forEach(entry => {
      const workId = entry.work?.id;
      const workRoleId = entry.workRole?.id;
      const profileId = entry.profile?.id;
      const key = `${workId}_${workRoleId}_${profileId}`;

      const isSpecialStatus = [
        REPERTOIRE_STATUSES.PREPARED,
        REPERTOIRE_STATUSES.UNPREPARED,
        REPERTOIRE_STATUSES.RETIRE,
      ].includes(entry.status);

      if (!seenWorkRoleProfiles.has(key) && isSpecialStatus) {
        seenWorkRoleProfiles.add(key);
        combinedEntries.push({
          ...entry,
          productionsTotal: 0,
          performancesTotal: 0,
          productionIds: [],
        });
      } else {
        const existing = combinedEntries.find(
          d => d.work?.id === workId && d.workRole?.id === workRoleId && d.profile?.id === profileId,
        );

        if (existing && isSpecialStatus) {
          existing.status = entry.status;
        }
      }
    });
  }

  combinedEntries.forEach(entry => {
    const { work } = entry || {};

    if (!work?.id) {
      return;
    }

    const composer = work.creators?.find(c => c.profession?.slug === 'composer');
    const composerName = composer?.profile?.shortName || composer?.profile?.name || 'Unknown';

    if (!groupedByComposer[composerName]) {
      groupedByComposer[composerName] = {
        composer: composer ? [composer] : [],
        data: [],
      };
      composerNamesSet.add(composerName);
    }

    const worksArray = groupedByComposer[composerName].data;
    let workGroup = worksArray.find(w => w.work.id === work.id);

    if (!workGroup) {
      workGroup = { work, data: [] };
      worksArray.push(workGroup);
    }

    const isDuplicate = workGroup.data.some(
      r => r.workRole?.id === entry.workRole?.id && r.profile?.id === entry.profile?.id,
    );

    if (!isDuplicate) {
      workGroup.data.push({
        ...entry,
        entityId,
        status: entry.status || null,
        productionsTotal: entry.productionsTotal || 0,
        performancesTotal: entry.performancesTotal || 0,
        productionIds: entry.productionIds || [],
      });

      workRoles.push(`${work.id}_${entry.workRole?.id || 0}`);
    }
  });

  Object.values(groupedByComposer).forEach(group =>
    group.data.sort((a, b) => (a.work?.name || '').localeCompare(b.work?.name || '')),
  );

  return {
    composerNames: [...composerNamesSet].sort(),
    data: groupedByComposer,
    workRoles,
  };
};

export const transformFeaturedHomepageArtists = response => {
  const { data } = response || {};

  return {
    ...response,
    data: data?.map(option => ({
      entity: {
        id: option?.id,
        name: option?.name,
        slug: option?.slug,
        headline: option?.headline,
        source_tag: option?.source_tag,
        image: condenseImageObject(option?.image),
        // Add data needed for 4-line profile structure
        organizations: option?.organizations || [],
        professions: option?.professions || [],
        workTypes: option?.workTypes || [],
      },
      entityType: ENTITY_TYPES.PROFILE,
    })),
  };
};

export const selectFeaturedHomepageArtists = response => {
  const { data } = response || {};

  return data?.map(option => ({
    ...option,
    entity: {
      ...option?.entity,
      image: expandImageObject(option?.entity?.image),
    },
  }));
};

// Helper transformer to add profession type information
export const addProfessionType = professionType => response => ({
  ...response,
  data: response?.data?.map(item => ({ ...item, type: professionType })) || [],
});

export const transformSplitByProfessionType = response => ({
  ...response,
  data: (response?.data || []).reduce(
    (acc, profession) => {
      if (profession?.parentProfession?.id) {
        if (profession?.parentProfession?.slug === PARENT_PROFESSION_SLUG.SINGER) {
          acc[PROFESSION_TYPES.VOICE_TYPE].push(getOption(profession, 'name', undefined, undefined, true));
        } else if (profession?.parentProfession?.slug === PARENT_PROFESSION_SLUG.INSTRUMENTALIST) {
          acc[PROFESSION_TYPES.INSTRUMENTALIST].push(getOption(profession, 'name', undefined, undefined, true));
        }

        return acc;
      }

      acc[PROFESSION_TYPES.PROFESSION].push(getOption(profession, 'name', undefined, undefined, true));
      return acc;
    },
    { [PROFESSION_TYPES.PROFESSION]: [], [PROFESSION_TYPES.VOICE_TYPE]: [], [PROFESSION_TYPES.INSTRUMENTALIST]: [] },
  ),
});
